import { convertDateForMappingQuestion, convertTimeForMappingQuestion } from '../../../utils/dateUtil';

import {
  mapToAdditionalExpense,
  mapToCancellationExpense,
  mapToCancellationRefundCompensationItem,
  mapToDelayedTravelRefundCompensationItem,
  mapToDelayedTravelExpense,
} from './expenseMapper';
import {
  stepCancellationAdditionalExpenseLookup as expenseLookup,
  emptyClaimCode,
} from './claimCodeMapper';
import { nullItem } from './sharedMapper';

function mapToParentSection() {
  return {};
}

function mapToQuestionnaire(data, claimCode) {
  return [
    {
      question: 'section5.claimForCancellationAmendmentOrDelay',
      answer: data.claimForCancellationAmendmentOrDelay,
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.claimForATravelDelay',
      answer: data.claimForATravelDelay,
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.dateCancellationOrAmendmentMade',
      answer: data.dateCancellationOrAmendmentMade,
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.departDate',
      answer: convertDateForMappingQuestion(data.departDate),
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.departTime',
      answer: convertTimeForMappingQuestion(data.departTime),
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.departedDate',
      answer: convertDateForMappingQuestion(data.departedDate),
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.departedTime',
      answer: convertTimeForMappingQuestion(data.departedTime),
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.receivedCompensation',
      answer: data.receivedCompensation,
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.delayedTravelReason',
      answer: data.delayedTravelReason,
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.reasonForCancellationOrAmendment',
      answer: mapReasonForCancellationOrAmendment(
        data.reasonForCancellationOrAmendment
      ),
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.dateOfExpense',
      answer: data.dateCompensationWasReceived,
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.description',
      answer: data.compensationDescription,
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.currency',
      answer: data.amountOfCompensationCurrency,
      claimTypeCode: claimCode,
    },
    {
      question: 'section5.clientClaim',
      answer: data.amountOfCompensation,
      claimTypeCode: claimCode,
    },
  ];
}

function getCancellationOrAmendmentExpenseClaimCode(data) {
  if (data.claimForCancellationAmendmentOrDelay === 'true') {
    return expenseLookup.claimForTripCancellationExpenses;
  }
  return emptyClaimCode;
}
function getAdditionalExpenseClaimCode(data) {
  if (data.areThereAnyAdditionalExpensesToBeClaimed === 'true') {
    return expenseLookup.claimForAdditionalExpenses;
  }
  return emptyClaimCode;
}
function getTravelDealyExpenseClaimCode(data) {
  if (data.claimForATravelDelay === 'true') {
    return expenseLookup.claimForATravelDelay;
  }
  return emptyClaimCode;
}

function getTravelDelayExpense(data) {
  const travelDelayClaimCode = getTravelDealyExpenseClaimCode(data);
  if (travelDelayClaimCode === emptyClaimCode) {
    return [];
  }
  return data.delayedTravelExpenses.map(
    mapToDelayedTravelExpense, travelDelayClaimCode
  );
}

function getTravelDelayRefundExpense(data) {
  const travelDelayClaimCode = getTravelDealyExpenseClaimCode(data);
  if (travelDelayClaimCode === emptyClaimCode) {
    return [];
  }
  return data.delayedTravelExpenses.map(
    mapToDelayedTravelRefundCompensationItem, travelDelayClaimCode
  ).filter(nullItem);
}

function getCancellationExpenses(data) {
  const cancellationAmendmentClaimCode = getCancellationOrAmendmentExpenseClaimCode(data);
  if (cancellationAmendmentClaimCode === emptyClaimCode) {
    return [];
  }
  return data.cancellationExpenses.map(
    mapToCancellationExpense, cancellationAmendmentClaimCode
  );
}

function getRefundCompensationExpenses(data) {
  const cancellationAmendmentClaimCode = getCancellationOrAmendmentExpenseClaimCode(data);
  if (cancellationAmendmentClaimCode === emptyClaimCode) {
    return [];
  }
  return data.cancellationExpenses.map(
    mapToCancellationRefundCompensationItem, cancellationAmendmentClaimCode
  ).filter(nullItem);
}

function getAdditionalExpenses(data) {
  const additionalExpenseClaimCode = getAdditionalExpenseClaimCode(data);

  if (additionalExpenseClaimCode === emptyClaimCode || data.areThereAnyAdditionalExpensesToBeClaimed !== 'true') {
    return [];
  }
  return data.additionalExpenses.map(
    mapToAdditionalExpense, additionalExpenseClaimCode
  );
}

function mapToExpenses(data) {
  const cancellationExpenses = getCancellationExpenses(data);
  const refundCompensationExpenses = getRefundCompensationExpenses(data);
  const additionalExpenses = getAdditionalExpenses(data);
  const delayedTravelExpenses = getTravelDelayExpense(data);
  const refundDelayedTravelExpenses = getTravelDelayRefundExpense(data);

  const expenses = [
    ...cancellationExpenses,
    ...refundCompensationExpenses,
    ...additionalExpenses,
    ...delayedTravelExpenses,
    ...refundDelayedTravelExpenses,
  ];

  return expenses;
}

function mapReasonForCancellationOrAmendment(reasonForCancellationOrAmendment) {
  const options = [];
  if (reasonForCancellationOrAmendment.medical) {
    options.push('medical');
  }
  if (reasonForCancellationOrAmendment.travel) {
    options.push('travel');
  }
  if (reasonForCancellationOrAmendment.weather) {
    options.push('weather');
  }
  if (reasonForCancellationOrAmendment.redundancy) {
    options.push('redundancy');
  }
  if (reasonForCancellationOrAmendment.other) {
    options.push(reasonForCancellationOrAmendment.otherDescription);
    // options.push('other');
  }
  // if (reasonForCancellationOrAmendment.otherDescription) {
  //   options.push(reasonForCancellationOrAmendment.otherDescription);
  // }

  return options.join(',');
}

export default {
  mapToParentSection,
  mapToQuestionnaire,
  mapToExpenses,
};



// WEBPACK FOOTER //
// ./app/containers/FastCover/dataMapper/stepCancellationAdditionalDataMapper.js